/**
 * 管理后台的登录态。
 *
 * 背景：管理后台与控制台会话完全分开（Cookie ap_admin_at / ap_admin_rt，刷新接口 /admin/auth/refresh），
 *   运营账号登录必须带两步验证动态码（PRD 5.1、7 章）。
 * 设计意图：
 *   - 启动时调 /admin/auth/me；401、403（未登录、非运营账号、IP 不在白名单）当未登录，回登录页并带上原地址。
 *     网络错误与 5xx（后端重启、入口代理 502）进 offline，显示连不上的提示并自动重试，会话保留，不送回登录页。
 *   - isAdmin 只用来隐藏写操作按钮：support 能查看、吊销 Key、锁定普通账号，其余写操作要 admin（后端校验）。
 *   - 语言跟随本机选择；没选过时跟随账号资料。
 * 关键约束：后端改了运营角色会吊销该账号的管理会话，这里靠 onAdminAuthLost 回登录页。
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { adminApi, onAdminAuthLost } from '@ap/api-client/admin'
import { isAuthFailure } from '@ap/api-client/http'
import type { Account } from '@ap/api-client/types'
import { hasSavedLocale, useI18n } from '@ap/i18n'
import { navigate, useOfflineRetry } from '@ap/ui'

interface AdminCtx {
  status: 'loading' | 'in' | 'out' | 'offline'
  account: Account | null
  isAdmin: boolean
  setAccount: (a: Account) => void
  /** offline 时立即再确认一次登录态。 */
  retry: () => void
  logout: () => Promise<void>
}

const Ctx = createContext<AdminCtx | null>(null)

export function loginUrl(): string {
  const here = window.location.pathname + window.location.search
  // 刷新失败的回调和 App 的未登录跳转会先后触发，已经在登录页时原样保留，免得把 return_to 冲掉。
  if (window.location.pathname === '/login') return here
  return here === '/' ? '/login' : `/login?return_to=${encodeURIComponent(here)}`
}

/** 只接受本站相对路径，防开放重定向。 */
export function goAfterLogin(returnTo: string | null): void {
  const safe = returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') && !returnTo.startsWith('/\\')
  navigate(safe ? returnTo : '/', { replace: true })
}

export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const { setLocale } = useI18n()
  const [status, setStatus] = useState<AdminCtx['status']>('loading')
  const [account, setAccountState] = useState<Account | null>(null)

  const setAccount = useCallback(
    (a: Account) => {
      setAccountState(a)
      setStatus('in')
      if (!hasSavedLocale()) setLocale(a.locale, false)
    },
    [setLocale],
  )

  const probing = useRef(false)
  const probe = useCallback(() => {
    if (probing.current) return
    probing.current = true
    adminApi
      .me()
      .then(setAccount)
      .catch((e) => {
        if (isAuthFailure(e)) {
          console.info('[admin] 未登录或会话已失效', e)
          setStatus('out')
        } else {
          console.warn('[admin] 后端暂时连不上，保留登录态并自动重试', e)
          setStatus('offline')
        }
      })
      .finally(() => {
        probing.current = false
      })
  }, [setAccount])

  useEffect(probe, [probe])
  useOfflineRetry(status === 'offline', probe)

  useEffect(
    () =>
      // 只改状态，跳不跳登录页交给 App：在登录页上刷新失败时不该再跳一次。
      onAdminAuthLost(() => {
        setAccountState(null)
        setStatus('out')
      }),
    [],
  )

  const logout = useCallback(async () => {
    try {
      await adminApi.logout()
    } catch (e) {
      console.warn('[admin] 登出请求失败，本地照样清掉登录态', e)
    }
    setAccountState(null)
    setStatus('out')
    navigate('/login', { replace: true })
  }, [])

  const value = useMemo<AdminCtx>(
    () => ({ status, account, isAdmin: account?.operator_role === 'admin', setAccount, retry: probe, logout }),
    [status, account, setAccount, probe, logout],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAdmin(): AdminCtx {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAdmin must be used inside <AdminSessionProvider>')
  return ctx
}
